/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from 'src/app/service/auth.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import {
  OTHER_COLLECTION_ID,
  QueryApiService,
} from 'src/app/shared/services/customer-api/query-api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, firstValueFrom, from, of } from 'rxjs';
import {
  catchError,
  concatMap,
  filter,
  finalize,
  switchMap,
} from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { QueryEditorComponent } from '../query-editor/query-editor.component';
import { QueryImportComponent } from '../query-import/query-import.component';
import { RoutingService } from 'src/app/service/routing.service';
import { QueryNameComponent } from '../query-name/query-name.component';
import {
  QueryDeleteChoice,
  QueryDeleteComponent,
  QueryDeleteData,
} from '../query-delete/query-delete.component';
import * as FileSaver from 'file-saver';
import {
  isSortedBy,
  resolveSortOrder,
  sortArrow,
} from 'src/app/shared/constants/sort-param-map';
import { ToneKind, valueTone } from 'src/app/shared/utils/list-tone';
import { SentimentService } from 'src/app/shared/services/data-api/sentiment.service';

const QUERY_FOLIO_SORT_MAP: Record<string, string> = {
  senti: '-sentiment.sentiment',
  '-senti': 'sentiment.sentiment',
  abc: 'query.name',
  '-abc': '-query.name',
  trend: '-sentiment.sentiment_delta',
  '-trend': 'sentiment.sentiment_delta',
  count: '-sentiment.count',
  '-count': 'sentiment.count',
  ratio: '-sentiment.volume_ratio',
  '-ratio': 'sentiment.volume_ratio',
};

// Sort key → what the cell holds, as a field of `item.sentiment`.
const TONED_COLUMNS: Record<string, { kind: ToneKind; field: string }> = {
  count: { kind: 'count', field: 'count' },
  ratio: { kind: 'ratio', field: 'volume_ratio' },
  senti: { kind: 'sentiment', field: 'sentiment' },
  trend: { kind: 'sentiment-delta', field: 'sentiment_delta' },
};

import { autocollection } from './preset-collection';
import * as XLSX from 'xlsx';

@Component({
  selector: 'app-query-folio',
  templateUrl: './query-folio.component.html',
  styleUrls: ['./query-folio.component.scss'],
})
export class QueryFolioComponent {
  items: any;

  itemsLoaded: any;

  isQueryAdded: boolean;

  /** Keeps a row's hover actions visible while a dialog it opened is open. */
  pinnedUid: string | null = null;

  query: any;
  myOrder: string;
  params: any;
  previousValue: any;

  constructor(
    public auth: AuthService,
    public featureFlagsService: FeatureFlagsService,
    private route: ActivatedRoute,
    private router: Router,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    public routing: RoutingService,
    private sentimentService: SentimentService,
    public queryApi: QueryApiService,
    private destroyRef: DestroyRef,
  ) {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.params = params;
        this.isQueryAdded = false;
        if (routing.reFresh(params, this.previousValue, ['update'])) {
          this.isQueryAdded = true;
        }
        if (
          routing.reFresh(params, this.previousValue, [
            'id',
            'groupId',
            'update',
            'time',
            'lang',
            'feed',
            'categories',
            'continents',
            'countries',
            'factuality',
            'temporality',
            'ranks',
            'panels',
          ])
        ) {
          let itemsCopy = null;
          let itemsCopyIds = [];
          if (this.items) {
            itemsCopy = JSON.parse(JSON.stringify(this.items));
            itemsCopyIds = this.items.map((item) => item.query.uid);
          }
          this.items = null;
          this.itemsLoaded = false;
          const query = [];
          if (this.queryApi.iquery) {
            if (!this.queryApi.iquery || this.queryApi.iquery.length === 0) {
              this.items = [];
            }
            this.items = [];
            const itemsLength = this.queryApi.iquery.length;
            let itemsCount = 0;
            const data = [];
            this.queryApi.iquery.forEach((item) => {
              const params = JSON.parse(JSON.stringify(this.params));
              params['iquery'] = Object.assign({}, item.query, {
                uid: item.uid,
              });
              // const request = this.yukkApi.isentiment(params, false);
              query.push(params);
              this.items.push({
                query: item,
              });
            });
            from(query)
              .pipe(
                concatMap((req, index) => {
                  if (
                    this.isQueryAdded &&
                    itemsCopy &&
                    this.queryApi.iquery &&
                    this.queryApi.iquery[index] &&
                    itemsCopyIds.includes(this.queryApi.iquery[index].uid) &&
                    !(
                      params.queryid &&
                      params.queryid === this.queryApi.iquery[index].uid
                    )
                  ) {
                    return of(
                      itemsCopy.filter((item) => {
                        return (
                          item.query.uid === this.queryApi.iquery[index].uid
                        );
                      })[0],
                    );
                  } else {
                    // A failed item must still emit — the completion condition
                    // counts emissions, so an error here froze the table.
                    return this.sentimentService
                      .getSentiment(req, false)
                      .pipe(catchError(() => of(undefined)));
                  }
                }),
              )
              .subscribe((queryItem2) => {
                const queryItem = queryItem2
                  ? JSON.parse(JSON.stringify(queryItem2))
                  : undefined;
                this.items[itemsCount].sentiment = queryItem?.sentiment;
                data.push(queryItem);
                itemsCount++;
                if (itemsCount === itemsLength) {
                  this.items = data.map((itom: any, index: number) => {
                    if (itom) {
                      itom['query'] = this.queryApi.iquery[index];
                      return itom;
                    } else {
                      return {
                        query: this.queryApi.iquery[index],
                      };
                    }
                  });
                  this.itemsLoaded = true;
                }
              });
          } else {
            this.items = [];
            // this.iQuery()
          }
        }

        this.previousValue = params;
        this.myOrder = resolveSortOrder(
          params.sort,
          QUERY_FOLIO_SORT_MAP,
          '-sentiment.count',
        );
      });
  }

  /** The "Other" pseudo-collection: queries that belong to no collection. */
  get isOther(): boolean {
    return this.params?.id === OTHER_COLLECTION_ID;
  }

  iEdit(item) {
    this.pinUntilClosed(
      item,
      this.dialog.open(QueryEditorComponent, { data: item.query }),
    ).subscribe();
  }

  trackByUid(_index: number, item) {
    return item.query?.uid;
  }

  /** Missing or unknown sort params order by volume, like myOrder. */
  private get sort(): string {
    const sort = this.params?.sort;
    return sort in QUERY_FOLIO_SORT_MAP ? sort : 'count';
  }

  isSorted(key: string): boolean {
    return isSortedBy(this.sort, key);
  }

  arrow(key: string): string {
    return this.isSorted(key) ? sortArrow(this.myOrder) : '';
  }

  /** Only the sorted column is tinted. */
  cellTone(key: string, item): string {
    if (!this.isSorted(key) || !item.sentiment) return '';
    const { kind, field } = TONED_COLUMNS[key];
    return 'sorted ' + valueTone(kind, item.sentiment[field]);
  }

  unpin(item) {
    if (this.pinnedUid === item.query.uid) this.pinnedUid = null;
  }

  private pinUntilClosed(item, dialogRef: { afterClosed(): Observable<any> }) {
    this.pinnedUid = item.query.uid;
    return dialogRef.afterClosed().pipe(finalize(() => this.unpin(item)));
  }

  private refresh(queryParams: Record<string, unknown> = {}) {
    this.router.navigate([], {
      queryParams: { update: Math.random(), ...queryParams },
      queryParamsHandling: 'merge',
    });
  }

  private collectionIsFull(extra = 1): boolean {
    const limit =
      this.featureFlagsService.featureFlags.queriesPerCollectionsLimit;
    if ((this.items?.length ?? 0) + extra <= limit) return false;
    this.snackBar.open(
      'Number of queries per collection is limited to ' + limit + '.',
      'OK',
      { duration: 5000 },
    );
    return true;
  }

  duplicateQuery(item) {
    if (!this.isOther && this.collectionIsFull()) return;
    const { name, description, query } = item.query;
    this.queryApi
      .addQuery({
        // 'default' is the backend's no-collection group.
        groups: [this.isOther ? 'default' : this.params.id],
        name: name + ' (copy)',
        description,
        query,
      })
      .subscribe(() => this.refresh());
  }

  exportQuery(item) {
    const { name, description, query } = item.query;
    const blob = new Blob([JSON.stringify({ name, description, query })], {
      type: 'application/json',
    });
    FileSaver.saveAs(blob, 'query_' + name + '.json');
  }

  deleteQuery(item) {
    const uid = item.query.uid;
    const collectionId = this.params.id;
    const data: QueryDeleteData = {
      query: item.query,
      collectionId,
      collections: this.queryApi.querys ?? [],
    };
    this.pinUntilClosed(item, this.dialog.open(QueryDeleteComponent, { data }))
      .pipe(
        filter((choice: QueryDeleteChoice) => !!choice),
        switchMap((choice: QueryDeleteChoice) =>
          choice === 'remove'
            ? this.queryApi.removeQueriesFromGroup([uid], collectionId)
            : this.queryApi.delQueries([uid]),
        ),
      )
      .subscribe(() => {
        const wasFilter = this.params.newsfeedFromQuery === uid;
        if (wasFilter) this.queryApi.queryForNewsfeed = null;
        this.refresh({
          queryid: null,
          ...(wasFilter ? { newsfeedFromQuery: null } : {}),
        });
      });
  }

  /** Imports query json files (the editor's export format) into this collection. */
  importQueryFiles(event): Promise<unknown> {
    const files: File[] = Array.from(event.target.files ?? []);
    if (files.length === 0 || this.collectionIsFull(files.length)) {
      return Promise.resolve();
    }
    return Promise.all(files.map((file) => file.text()))
      .then((texts) =>
        texts.map((text) => {
          const { name, description, query } = JSON.parse(text);
          // A collection export ({ name, content }) has no query of its own.
          if (!name || !query || typeof query !== 'object') {
            throw new Error('Not a query file');
          }
          return { groups: [this.params.id], name, description, query };
        }),
      )
      .then((queries) => firstValueFrom(this.queryApi.addQueries(queries)))
      .then(() => this.refresh())
      .catch(() =>
        this.snackBar.open(
          'Could not import: choose query files exported from Cockpit.',
          'OK',
          { duration: 5000 },
        ),
      );
  }

  iChart(item) {
    this.router.navigate(['../chart'], {
      queryParams: {
        id: item.query.uid,
        newsfeedFromQuery: null,
        queryid: null,
        groupId: this.params.groupId,
      },
      queryParamsHandling: 'merge',
      relativeTo: this.route,
    });
  }

  /**
   * add tag to the url on item click
   */
  addTag(item) {
    if (this.params.newsfeedFromQuery === item.query.uid) {
      this.queryApi.queryForNewsfeed = null;
    } else {
      this.queryApi.queryForNewsfeed = item;
    }
    this.router.navigate([], {
      queryParams: {
        newsfeedFromQuery:
          this.params.newsfeedFromQuery === item.query.uid
            ? null
            : item.query.uid,
        newstype: null,
        queryid: null,
      },
      queryParamsHandling: 'merge',
      replaceUrl: false,
    });
  }

  iImport(event, type) {
    if (event === null && type === 'query') {
      if (!this.collectionIsFull()) {
        const currentCollection = this.queryApi.querys
          ? this.queryApi.querys.filter((item) => {
              return item.uid === this.params.id;
            })[0]
          : {};
        this.dialog.open(QueryImportComponent, {
          data: {
            collection: currentCollection,
            queries: this.items.map((element) => {
              return element.query;
            }),
          },
        });
      }
    } else if (event !== null && type === 'collection') {
      if (
        this.queryApi.querys &&
        event.target.files &&
        this.queryApi.querys.length + event.target.files.length >
          this.featureFlagsService.featureFlags.collectionsLimit
      ) {
        this.snackBar.open(
          'Number of collections is limited to ' +
            this.featureFlagsService.featureFlags.collectionsLimit +
            '.',
          'OK',
          { duration: 5000 },
        );
      } else {
        if (event.target.files && event.target.files[0]) {
          for (let i = 0; i < event.target.files.length; i++) {
            const reader = new FileReader();
            reader.onload = (e: any) => {
              const result = JSON.parse(e.target.result);
              if (
                result.content &&
                result.content.length >
                  this.featureFlagsService.featureFlags
                    .queriesPerCollectionsLimit
              ) {
                this.snackBar.open(
                  'Number of queries per collection is limited to ' +
                    this.featureFlagsService.featureFlags
                      .queriesPerCollectionsLimit +
                    '.',
                  'OK',
                  { duration: 5000 },
                );
              } else {
                this.queryApi
                  .addGroupQuery(result.name)
                  .subscribe((result2) => {
                    const queriesArray = [];
                    result.content.forEach((query) => {
                      queriesArray.push(
                        Object.assign({}, query, {
                          groups: [result2.id],
                        }),
                      );
                    });
                    this.queryApi.addQueries(queriesArray).subscribe(() => {
                      this.router.navigate([], {
                        queryParams: {
                          id: result2.id,
                          groupId: result2.id,
                          update: Math.random(),
                          queryid: null,
                          newsfeedFromQuery: null,
                        },
                        queryParamsHandling: 'merge',
                      });
                    });
                  });
              }
            };
            reader.readAsText(event.target.files[i]);
          }
        }
      }
    }
  }

  iQuery() {
    if (!this.collectionIsFull()) {
      this.dialog.open(QueryEditorComponent, {});
    }
  }

  iCreate() {
    if (
      this.queryApi.querys &&
      this.queryApi.querys.length >=
        this.featureFlagsService.featureFlags.collectionsLimit
    ) {
      this.snackBar.open(
        'Number of collections is limited to ' +
          this.featureFlagsService.featureFlags.collectionsLimit +
          '.',
        'OK',
        { duration: 5000 },
      );
    } else {
      this.dialog.open(QueryNameComponent, {
        // data: {}
      });
    }
  }

  // inTab(uid) {
  //   this.router.navigate([], {
  //     queryParams: {
  //       id: uid,
  //       groupId: uid
  //     },
  //     queryParamsHandling: 'merge',
  //     relativeTo: this.route
  //   });
  // }

  iExport(type) {
    /*if (type === 'events') {
      this.yukkApi.exportEvent(this.params, 'query').subscribe(result => {
        FileSaver.saveAs(result, 'event_export.xlsx');
      });
    } else */
    if (type === 'collection') {
      const collectionPure = this.queryApi.querys
        .filter((collection) => {
          return collection.uid === this.params.groupId;
        })
        .map((element) => {
          return {
            name: element.name,
            content: element.content.map((query) => {
              return {
                name: query.name,
                query: query.query,
              };
            }),
          };
        })[0];
      const collectionString = JSON.stringify(collectionPure);
      const blob = new Blob([collectionString], { type: 'application/json' });
      FileSaver.saveAs(blob, 'collection_' + collectionPure.name + '.json');
    } else if (type === 'stats') {
      if (this.items && this.items.length > 0) {
        const date = new Date();
        const displayDate =
          date.getFullYear() +
          '-' +
          date.getMonth() +
          '-' +
          date.getDate() +
          ' ' +
          date.getHours() +
          '-' +
          date.getMinutes() +
          '-' +
          date.getSeconds();
        const doubleArrayData = [['Name', 'Vol', 'ΔVol', 'Sent', 'ΔSent']];
        this.items.forEach((query) => {
          doubleArrayData.push([
            query.query && query.query.name ? query.query.name : '',
            query.sentiment && query.sentiment.count
              ? query.sentiment.count
              : '',
            query.sentiment && query.sentiment.volume_ratio
              ? query.sentiment.volume_ratio
              : '',
            query.sentiment && query.sentiment.sentiment
              ? query.sentiment.sentiment
              : '',
            query.sentiment && query.sentiment.sentiment_delta
              ? query.sentiment.sentiment_delta
              : '',
          ]);
        });
        /* generate worksheet */
        const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(doubleArrayData);
        /* generate workbook and add the worksheet */
        const wb: XLSX.WorkBook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
        /* save to file */
        XLSX.writeFile(
          wb,
          'Collection statistics from ' + displayDate + '.xlsx',
        );
      }
    }
  }

  /**
   * apply chosen sorting option
   */
  goSort(value) {
    if (this.sort === value) {
      this.router.navigate([], {
        queryParams: { sort: '-' + value },
        queryParamsHandling: 'merge',
        replaceUrl: false,
      });
    } else {
      this.router.navigate([], {
        queryParams: { sort: value },
        queryParamsHandling: 'merge',
        replaceUrl: false,
      });
    }
  }

  loadSampleCollections() {
    for (let i = 0; i < autocollection.length; i++) {
      const result = autocollection[i];
      this.queryApi.addGroupQuery(result.name).subscribe((result2) => {
        const queriesArray = [];
        result.content.forEach((query) => {
          queriesArray.push(
            Object.assign({}, query, {
              groups: [result2.id],
            }),
          );
        });
        this.queryApi.addQueries(queriesArray).subscribe(() => {
          this.router.navigate([], {
            queryParams: {
              id: result2.id,
              groupId: result2.id,
              update: Math.random(),
              queryid: null,
              newsfeedFromQuery: null,
            },
            queryParamsHandling: 'merge',
          });
        });
      });
    }
  }
}
